// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { Shipment, shipmentSchema } from '@/shipment/api/types';
import { z } from 'zod';

/**
 * Schema for a single line item quantity edit request.
 */
const editLineItemRequestSchema = z.object({
  itemId: z.string(),
  newQuantity: z.number(),
});

/**
 * Schema for the edit line items request body.
 */
export const editLineItemsRequestSchema = z.object({
  items: z.array(editLineItemRequestSchema),
});

export type EditLineItemsRequest = z.infer<typeof editLineItemsRequestSchema>;

/**
 * Schema for validating the response from the edit line items API endpoint.
 * Response structure: { data: { shipment: {...} }, metadata: {...} }
 */
const editLineItemsResponseSchema = z.object({
  data: z.object({
    shipment: shipmentSchema,
  }),
  metadata: responseMetadataSchema,
});

/**
 * Edits line item quantities on a received shipment.
 * Only bulk items (parts and non-inventory products) can be edited.
 *
 * @param shipmentKey - The key of the shipment to edit
 * @param request - The edit request containing item changes
 * @returns The updated shipment
 */
export async function editLineItemQuantities(
  shipmentKey: string,
  request: EditLineItemsRequest
): Promise<Shipment> {
  const url = `/v1/shipment/${shipmentKey}/line-items/edit`;
  const response = await api.post(url, request);

  const { data } = await parseResponse(
    editLineItemsResponseSchema,
    response.data,
    {
      schemaName: 'EditLineItems',
      endpoint: url,
    }
  );

  return data.shipment;
}
